// FlowsArchive — Wansight "Reports » Tools » Flows" parity (Plan P2.1).
//
// Lets the operator search the binary wgflow.* archive on disk for
// individual records matching a filter. The dashboard's existing /flows
// page (Live Flows) shows a rolling buffer of flows the sensor saw in
// the last few minutes; this page reaches further back into the
// 5-minute rotated archive that sfsensor writes to /opt/sflow/spool/.
//
// Why this exists: when an IP shows up suspiciously in a top-talker
// widget, the operator needs to drill into "what was that IP actually
// doing — which ports, which other hosts, which AS?" before deciding
// whether to mitigate. TSDB only carries 5 s aggregates; the archive
// has every flow.
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// All times shown/entered on this page are in Indochina Time (UTC+7).
const TZ = "Asia/Ho_Chi_Minh";
import type { ColumnsType } from "antd/es/table";
import { api, type Decoder, type FlowSearchRow, type Sensor } from "../api";

// fmtBytes / fmtPkts — tiny formatters with K/M/G suffixes. Same shape
// as the helpers on the Sensors page so the UI feels consistent across
// pages that surface raw counters.
function fmtBytes(v: number): string {
  if (!v) return "0";
  if (v >= 1e9) return (v / 1e9).toFixed(2) + " G";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(v);
}

function fmtPkts(v: number): string {
  if (!v) return "0";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(v);
}

// Map IANA protocol numbers to the names operators recognise without
// having to remember which is which. Anything not in the table renders
// as the raw number so we don't silently hide unexpected protocols.
const PROTO_NAMES: Record<number, string> = {
  1: "ICMP",
  6: "TCP",
  17: "UDP",
  47: "GRE",
  50: "ESP",
  51: "AH",
  58: "ICMPv6",
  132: "SCTP",
};

function protoName(p: number): string {
  return PROTO_NAMES[p] ?? String(p);
}

// downloadCSV builds a CSV blob from the result rows and triggers a
// browser download. Done client-side so the operator can re-export
// after manual filtering in the UI without a second backend round-trip.
//
// Format: one header line + one row per flow with columns matching the
// table on the page. Bytes/packets stay raw (operator can post-process
// in Excel) instead of getting the K/M/G suffix the table shows.
function downloadCSV(rows: FlowSearchRow[]) {
  const header = [
    "ts_unix",
    "ts_iso",
    "src_ip",
    "src_port",
    "dst_ip",
    "dst_port",
    "protocol",
    "tcp_flags",
    "bytes",
    "packets",
    "sampling_rate",
    "input_if",
    "output_if",
    "src_as",
    "dst_as",
  ].join(",");
  const lines = rows.map((r) =>
    [
      r.ts_unix,
      dayjs.unix(r.ts_unix).tz(TZ).format("YYYY-MM-DD HH:mm:ss"),
      r.src_ip,
      r.src_port,
      r.dst_ip,
      r.dst_port,
      r.protocol,
      r.tcp_flags,
      r.bytes,
      r.packets,
      r.sampling_rate,
      r.input_if ?? 0,
      r.output_if ?? 0,
      r.src_as ?? 0,
      r.dst_as ?? 0,
    ].join(",")
  );
  const blob = new Blob([header + "\n" + lines.join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `flows-${dayjs().tz(TZ).format("YYYYMMDD-HHmmss")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface FormValues {
  sensor_id: number;
  range: [Dayjs, Dayjs];
  ip?: string;
  src_ip?: string;
  dst_ip?: string;
  decoder_name?: string;
  port?: number;
  src_port?: number;
  dst_port?: number;
  min_bytes?: number;
  limit?: number;
}

export default function FlowsArchive() {
  const [form] = Form.useForm<FormValues>();
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [decoders, setDecoders] = useState<Decoder[]>([]);
  const [rows, setRows] = useState<FlowSearchRow[]>([]);
  const [meta, setMeta] = useState<{
    scanned: number;
    matched: number;
    files_read: number;
    truncated: boolean;
    duration_ms: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  // Load the sensor list once. We need names + ids for the dropdown,
  // and we also default the form to the first sensor so the operator
  // can hit Search immediately with sensible defaults.
  useEffect(() => {
    (async () => {
      try {
        const [s, all] = await Promise.all([api.sensors(), api.decoders()]);
        setSensors(s);
        // Exclude "IP" (catch-all) — too broad to be useful as a filter.
        setDecoders(all.filter((d) => d.enabled && d.name !== "IP"));
        form.setFieldsValue({
          sensor_id: 0,
          range: [dayjs().tz(TZ).subtract(15, "minute"), dayjs().tz(TZ)],
          limit: 200,
        });
      } catch (e) {
        if (e instanceof Error) message.error(e.message);
      }
    })();
  }, [form]);

  async function onSearch(values: FormValues) {
    if (!values.range || values.range.length !== 2) {
      message.error("Pick a time range");
      return;
    }
    setBusy(true);
    const base = {
      start_unix: values.range[0].unix(),
      end_unix:   values.range[1].unix(),
      ip:         values.ip        || undefined,
      src_ip:     values.src_ip    || undefined,
      dst_ip:     values.dst_ip    || undefined,
      decoder_name: values.decoder_name || undefined,
      port:       values.port      || undefined,
      src_port:   values.src_port  || undefined,
      dst_port:   values.dst_port  || undefined,
      min_bytes:  values.min_bytes || undefined,
      limit:      values.limit     || 200,
    };
    try {
      // sensor_id=0 means "all" — fan out to every sensor in parallel.
      const targets = values.sensor_id === 0
        ? sensors.map((s) => s.id)
        : [values.sensor_id];

      const results = await Promise.allSettled(
        targets.map((id) => api.flowSearch({ ...base, sensor_id: id }))
      );

      let scanned = 0, matched = 0, files_read = 0, truncated = false, duration_ms = 0;
      const allRows: FlowSearchRow[] = [];

      for (const r of results) {
        if (r.status === "fulfilled") {
          allRows.push(...r.value.rows);
          scanned    += r.value.scanned;
          matched    += r.value.matched;
          files_read += r.value.files_read;
          truncated   = truncated || r.value.truncated;
          duration_ms = Math.max(duration_ms, r.value.duration_ms);
        }
      }

      // Sort merged rows newest-first and honour the per-search row cap.
      const limit = base.limit ?? 200;
      allRows.sort((a, b) => b.ts_unix - a.ts_unix);
      const trimmed = allRows.slice(0, limit);

      setRows(trimmed);
      setMeta({ scanned, matched, files_read, truncated, duration_ms });

      if (trimmed.length === 0) {
        message.info(
          `Scanned ${scanned.toLocaleString()} record(s) in ${files_read} file(s); no matches.`
        );
      }
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  const columns: ColumnsType<FlowSearchRow> = useMemo(
    () => [
      {
        title: "Time",
        dataIndex: "ts_unix",
        width: 170,
        render: (v: number) => dayjs.unix(v).tz(TZ).format("YYYY-MM-DD HH:mm:ss"),
        sorter: (a, b) => a.ts_unix - b.ts_unix,
        defaultSortOrder: "descend",
      },
      {
        title: "Source",
        key: "src",
        render: (_: unknown, r) => (
          <span>
            <code>{r.src_ip}</code>
            <span style={{ color: "var(--muted)" }}>:{r.src_port}</span>
          </span>
        ),
      },
      {
        title: "Destination",
        key: "dst",
        render: (_: unknown, r) => (
          <span>
            <code>{r.dst_ip}</code>
            <span style={{ color: "var(--muted)" }}>:{r.dst_port}</span>
          </span>
        ),
      },
      {
        title: "Proto",
        dataIndex: "protocol",
        width: 80,
        render: (v: number) => <Tag>{protoName(v)}</Tag>,
      },
      {
        title: "Bytes",
        dataIndex: "bytes",
        width: 100,
        align: "right",
        render: (v: number) => fmtBytes(v),
        sorter: (a, b) => a.bytes - b.bytes,
      },
      {
        title: "Packets",
        dataIndex: "packets",
        width: 90,
        align: "right",
        render: (v: number) => fmtPkts(v),
        sorter: (a, b) => a.packets - b.packets,
      },
      {
        title: "AS (src→dst)",
        key: "as",
        width: 130,
        render: (_: unknown, r) =>
          r.src_as || r.dst_as ? (
            <span style={{ color: "var(--muted)" }}>
              {r.src_as ?? 0} → {r.dst_as ?? 0}
            </span>
          ) : (
            <span style={{ color: "var(--muted)" }}>—</span>
          ),
      },
      {
        title: "If (in→out)",
        key: "if",
        width: 110,
        render: (_: unknown, r) =>
          r.input_if || r.output_if ? (
            <span style={{ color: "var(--muted)" }}>
              {r.input_if ?? 0} → {r.output_if ?? 0}
            </span>
          ) : (
            <span style={{ color: "var(--muted)" }}>—</span>
          ),
      },
    ],
    []
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card title="Flow Archive Search" size="small">
        <Form
          form={form}
          layout="vertical"
          onFinish={onSearch}
          initialValues={{ sensor_id: 0, limit: 200 }}
        >
          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item
                name="sensor_id"
                label="Sensor"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 0, label: "All sensors" },
                    ...sensors.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                name="range"
                label="Time range (+07:00)"
                rules={[{ required: true }]}
                tooltip="Server caps each search at 24 h. The wgflow archive rotates every 5 min, so smaller ranges return faster."
              >
                <DatePicker.RangePicker
                  showTime
                  style={{ width: "100%" }}
                  presets={[
                    { label: "Last 5 min",  value: [dayjs().tz(TZ).subtract(5, "minute"),  dayjs().tz(TZ)] },
                    { label: "Last 15 min", value: [dayjs().tz(TZ).subtract(15, "minute"), dayjs().tz(TZ)] },
                    { label: "Last 1 h",    value: [dayjs().tz(TZ).subtract(1, "hour"),    dayjs().tz(TZ)] },
                    { label: "Last 6 h",    value: [dayjs().tz(TZ).subtract(6, "hour"),    dayjs().tz(TZ)] },
                    { label: "Last 24 h",   value: [dayjs().tz(TZ).subtract(24, "hour"),   dayjs().tz(TZ)] },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="decoder_name" label="Protocol">
                <Select
                  allowClear
                  placeholder="any"
                  showSearch
                  options={decoders.map((d) => ({ value: d.name, label: d.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                name="limit"
                label="Limit"
                tooltip="Max rows returned. Server hard cap 5000."
              >
                <InputNumber min={1} max={5000} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item
                name="ip"
                label="IP (src OR dst)"
                tooltip="Match against either side of the flow."
              >
                <Input placeholder="e.g. 103.142.25.198" allowClear />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="src_ip" label="Source IP">
                <Input placeholder="exact-match" allowClear />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="dst_ip" label="Destination IP">
                <Input placeholder="exact-match" allowClear />
              </Form.Item>
            </Col>
            <Col xs={8} md={2}>
              <Form.Item
                name="port"
                label="Port"
                tooltip="src OR dst port"
              >
                <InputNumber min={0} max={65535} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={8} md={2}>
              <Form.Item name="src_port" label="src">
                <InputNumber min={0} max={65535} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={8} md={2}>
              <Form.Item name="dst_port" label="dst">
                <InputNumber min={0} max={65535} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={2}>
              <Form.Item
                name="min_bytes"
                label="Min bytes"
                tooltip="Drop tiny flows from the result (helps spot real heavy hitters)."
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              htmlType="submit"
              loading={busy}
            >
              Search
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={rows.length === 0}
              onClick={() => downloadCSV(rows)}
            >
              Export CSV ({rows.length})
            </Button>
            {meta && (
              <span style={{ color: "var(--muted)", fontSize: 12 }}>
                Scanned {meta.scanned.toLocaleString()} record(s) in{" "}
                {meta.files_read} file(s) · matched{" "}
                {meta.matched.toLocaleString()} · {meta.duration_ms} ms
              </span>
            )}
          </Space>
        </Form>
      </Card>

      {meta?.truncated && (
        <Alert
          type="warning"
          showIcon
          message="Result truncated"
          description="The server stopped scanning before reading the entire range (hit limit or scan cap). Narrow the time range or add tighter filters to see all matches."
        />
      )}

      <Card size="small" bodyStyle={{ padding: 0 }}>
        <Table<FlowSearchRow>
          rowKey={(r) =>
            `${r.ts_unix}-${r.src_ip}-${r.src_port}-${r.dst_ip}-${r.dst_port}-${r.protocol}`
          }
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
          locale={{
            emptyText:
              "No results yet. Pick a sensor + time range and click Search to query the wgflow.* archive.",
          }}
        />
      </Card>
    </div>
  );
}
